import { APIClient, ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { Token } from '@frontend/authentication-v2';
import { CreateProduct, IProduct, ProductListResponse, ProductQueryParams, UpdateProduct } from '@frontend/product/types';

export class ProductClient extends APIClient {
    public static readonly endpoint = '/product-api/v1';

    public static async fetchProducts(queryParams?: ApiQueryParams<ProductQueryParams>, withCredentials?: Token): Promise<ProductListResponse> {
        return await this.apiPaginated<ProductListResponse, ProductQueryParams>(`${this.endpoint}/products`, queryParams, withCredentials);
    }

    public static async fetchCategoryProducts(queryParams?: ApiQueryParams<ProductQueryParams & { group_id: string }>): Promise<ProductListResponse> {
        return await this.apiPaginated<ProductListResponse, ProductQueryParams>(`${this.endpoint}/product-category-products`, queryParams);
    }

    public static async resolveProducts(productIds: string[], withCredentials?: Token): Promise<ProductListResponse> {
        const response = await this.post(`${this.endpoint}/products-resolve`, { ids: productIds }, true, withCredentials);
        return await this.handleResponse<ProductListResponse>(response);
    }

    public static async fetchAccountProducts(accountId: string, queryParams?: ApiQueryParams<DefaultQueryParams>): Promise<ProductListResponse> {
        return await this.apiPaginated<ProductListResponse, DefaultQueryParams>(`${this.endpoint}/accounts/${accountId}/products`, queryParams);
    }

    public static async postProduct(accountId: string, product: CreateProduct): Promise<IProduct> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/products`, product);
        return await this.handleResponse<IProduct>(response);
    }

    public static async fetchProduct(accountId: string, productId: string): Promise<IProduct> {
        const response = await this.fetch(`${this.endpoint}/accounts/${accountId}/products/${productId}`);
        return await this.handleResponse<IProduct>(response);
    }

    public static async fetchProductsNotInGroup(
        accountId: string,
        groupId: string,
        queryParams?: ApiQueryParams<DefaultQueryParams>
    ): Promise<ProductListResponse> {
        return await this.apiPaginated<ProductListResponse, DefaultQueryParams>(
            `${this.endpoint}/accounts/${accountId}/groups/${groupId}/products`,
            queryParams
        );
    }

    public static async patchProduct(accountId: string, productId: string, product: UpdateProduct): Promise<IProduct> {
        const response = await this.patch(`${this.endpoint}/accounts/${accountId}/products/${productId}`, product);
        return await this.handleResponse<IProduct>(response);
    }

    public static async deleteProduct(accountId: string, productId: string): Promise<void> {
        const response = await this.delete(`${this.endpoint}/accounts/${accountId}/products/${productId}`);
        return await this.handleVoidResponse(response);
    }

    public static async deleteProducts(productIds: string[]): Promise<void> {
        const response = await this.post(`${this.endpoint}/products-delete`, { ids: productIds });
        return await this.handleVoidResponse(response);
    }

    public static async fetchProductsNotInKit(accountId: string, queryParams?: ApiQueryParams<ProductQueryParams>): Promise<ProductListResponse> {
        return await this.apiPaginated<ProductListResponse, ProductQueryParams>(`${this.endpoint}/accounts/${accountId}/products-not-in-kit`, queryParams);
    }

    public static async postIoTSeed(accountId: string, iotId: string, body: { account_id: string }): Promise<void> {
        const response = await this.post(`${this.endpoint}/accounts/${accountId}/iots/${iotId}/seed`, body);
        return this.handleResponse(response);
    }
}
